import { useEffect } from 'react';
import {
  DimensionValue,
  Image,
  ImageBackground,
  Linking,
  Platform,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import themes from '_/constants/themes';
import { Header, Text } from '_/components';
import ContainerWidthLimit from '_/components/ContainerWidthLimit';
import { useNavigation } from '@react-navigation/native';
import { fontSizes } from '_/config/theme';
import { useDispatch, useSelector } from 'react-redux';
import { HostCardNumbersModel, HostsModel } from '@w3lcome/types';
import {
  AddGoogleWalletHIDButton,
  AddAppleWalletHIDButton,
  WalletButon,
} from '_/components/AddWalletHID';
import { useTranslation } from 'react-i18next';
import { useActiveAppState } from '_/hooks/useActiveAppState';
import { updateHostCardNumberRequest } from '_/store/modules/host/actions';
import hidProvisioningApi from '_/services/api/hid-provisioning';

export default function HIDCard() {
  const { t: translate } = useTranslation();
  const { isActive: appIsActive } = useActiveAppState();
  const dispatch = useDispatch();
  const { goBack } = useNavigation<any>();

  const host: HostsModel = useSelector((state: any) => state.host);

  const HIDMobileBtn = () => {
    return (
      <TouchableOpacity
        style={styles.button}
        onPress={() =>
          Linking.openURL(
            Platform.OS === 'ios'
              ? 'https://apps.apple.com/br/app/hid-mobile-access/id843238107'
              : 'https://play.google.com/store/apps/details?id=com.hidglobal.mobilekeys.android.v3'
          )
        }
      >
        <Text fontType="semibold" style={styles.text}>
          {translate('provisioning.openMobileAccess')}
        </Text>
      </TouchableOpacity>
    );
  };

  const cardItem = (
    card: HostCardNumbersModel,
    index: number,
    width: DimensionValue
  ): React.ReactNode => {
    const tokenStatus = card?.provisioningIssuanceToken?.status;

    return (
      <View
        key={index}
        style={[
          styles.boxContainer,
          {
            marginRight: index === (host.hostCardNumbers as []).length - 1 ? 0 : 16,
            width,
          },
        ]}
      >
        <View style={[styles.cardContainer]}>
          <ImageBackground source={themes.images.cardBackground} style={[styles.imgBackground]}>
            <Image
              resizeMode="contain"
              source={themes.images.invenziLogo}
              style={{ height: 60, width: 100, position: 'absolute', right: 16, top: 0 }}
            />

            <View style={{ right: 1, top: '32%', marginLeft: 20 }}>
              <Text size={fontSizes.lg} fontType="bold" color={themes.colors.primary}>
                {host.name}
              </Text>
              <Text size={fontSizes.md2} fontType="semibold" color={themes.colors.graySecondary}>
                {card.name || card.cardNumber}
              </Text>
            </View>

            <Image
              resizeMode="contain"
              source={themes.images.hidLogo}
              style={{ height: 48, width: 100, position: 'absolute', left: 0, bottom: 8 }}
            />
          </ImageBackground>
        </View>
        {card.issuanceToken &&
          (Platform.OS === 'ios' ? (
            <>
              {(!tokenStatus || tokenStatus === 'INITIATED') && (
                <AddAppleWalletHIDButton
                  issuanceToken={card.issuanceToken}
                  hostCardNumberId={card.id}
                  styles={[styles.addWalletBtn]}
                />
              )}
              {tokenStatus === 'ACTIVE' && (
                <WalletButon
                  content={translate('provisioning.viewInAppleWallet')}
                  styles={[styles.addWalletBtn]}
                  onPress={() => Linking.openURL('wallet://')}
                  isApple
                />
              )}
            </>
          ) : (
            <>
              {(!tokenStatus || tokenStatus === 'INITIATED') && (
                <AddGoogleWalletHIDButton styles={[styles.addWalletBtn]} hostCard={card} />
              )}
              {tokenStatus === 'ACTIVE' && (
                <WalletButon
                  content={translate('provisioning.viewInGoogleWallet')}
                  styles={[styles.addWalletBtn]}
                  // open google wallet app
                  onPress={() => Linking.openURL('https://pay.google.com/gp/v/save')}
                />
              )}
            </>
          ))}
        {!card.issuanceToken && <HIDMobileBtn />}
      </View>
    );
  };

  if (!host.hostCardNumbers?.length) {
    return (
      <ContainerWidthLimit>
        <View style={styles.container}>
          <Header handleBack={() => goBack()} title={translate(`HID Credential Mobile`)} />
          <View style={styles.content}>
            <Text size={fontSizes.md2} fontType="semibold" color={themes.colors.danger}>
              {translate('provisioning.noCardsRegistered')}
            </Text>
          </View>
        </View>
      </ContainerWidthLimit>
    );
  }

  useEffect(() => {
    if (appIsActive && host?.hostCardNumbers) {
      host.hostCardNumbers.forEach((card) => {
        const status = card?.provisioningIssuanceToken?.status;

        if (status !== 'ACTIVE' && status !== 'REVOKED') {
          hidProvisioningApi
            .patch(card.id)
            .then((response: any) => {
              const updatedProvisioning = Array.isArray(response) ? response[0] : response;

              if (updatedProvisioning?.status && updatedProvisioning.status !== status) {
                dispatch(
                  updateHostCardNumberRequest({
                    id: card.id,
                    provisioningIssuanceToken: updatedProvisioning,
                  })
                );
              }
            })
            .catch((err) => {
              console.log('- Sync error: ', err);
            });
        }
      });
    }
  }, [appIsActive, host?.hostCardNumbers, dispatch]);

  return (
    <ContainerWidthLimit>
      <View style={styles.container}>
        <Header handleBack={() => goBack()} title={`HID Credential Mobile`} />

        <View style={styles.content}>
          {host.hostCardNumbers.length === 1 ? (
            cardItem(host.hostCardNumbers[0], 0, '100%')
          ) : (
            <View>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                {host.hostCardNumbers.map((card, index) => cardItem(card, index, 300))}
              </ScrollView>
            </View>
          )}
        </View>
      </View>
    </ContainerWidthLimit>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: themes.colors.white,
    flex: 1,
  },
  content: {
    flex: 1,
    padding: 16,
    gap: 48,
  },
  button: {
    alignItems: 'center',
    alignSelf: 'center',
    backgroundColor: '#000',
    borderRadius: 8,
    flexDirection: 'row',
    justifyContent: 'center',
    maxWidth: 250,
    paddingVertical: 12,
    paddingHorizontal: 20,
    width: '80%',
  },
  logo: {
    width: 24,
    height: 24,
    marginRight: 12,
  },
  text: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '500',
  },
  boxContainer: {
    backgroundColor: '#fff',
    height: 300,
    shadowColor: '#0000',
  },
  cardContainer: {
    borderRadius: 10,
    height: '65%',
    elevation: 1.5,
    marginBottom: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 6,
  },
  imgBackground: {
    height: '100%',
    width: '100%',
  },
  addWalletBtn: {
    alignSelf: 'center',
    maxWidth: 300,
    minHeight: 52,
    marginBottom: 12,
  },
});
